<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { Download, FileDown, RotateCw } from '@lucide/vue';
import { CATALOGO_EXPORT, type Esportazione, type StatoEsportazione } from '@evolfin/shared';
import { EvolfinApiError } from '@/api/client';
import { useAvvisi } from '@/composables/useAvvisi';
import { useInvalidation } from '@/composables/useInvalidation';
import { formattaData, formattaDataOra, formattaNumero } from '@/utils/formato';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import StatoCaricamento from '@/components/ui/StatoCaricamento.vue';
import StatoVuoto from '@/components/ui/StatoVuoto.vue';
import { elencoExport, linkDownloadExport, riprovaExport } from '../api';
import { exportKeys } from '../query-keys';

definePage({ meta: { titolo: 'I miei export' } });

const avvisi = useAvvisi();
const invalidate = useInvalidation('export');

const IN_LAVORAZIONE: readonly StatoEsportazione[] = ['in_coda', 'in_corso'];

const { data: esportazioni, isLoading } = useQuery({
  queryKey: exportKeys.mine(),
  queryFn: elencoExport,
  // Finché qualcosa si sta preparando l'elenco si aggiorna da sé.
  refetchInterval: (q) =>
    q.state.data?.some((e) => IN_LAVORAZIONE.includes(e.stato)) ? 3000 : false,
});

const STATI: Record<StatoEsportazione, { etichetta: string; classi: string }> = {
  in_coda: { etichetta: 'In coda', classi: 'bg-soft text-ink-secondary' },
  in_corso: {
    etichetta: 'In preparazione',
    classi: 'bg-[var(--surface-warning-soft)] text-[var(--color-warning-text)]',
  },
  pronto: {
    etichetta: 'Pronto',
    classi: 'bg-[var(--surface-accent-soft)] text-[var(--color-primary)]',
  },
  fallito: {
    etichetta: 'Non riuscito',
    classi: 'bg-[var(--surface-danger-soft)] text-[var(--color-danger-text)]',
  },
  scaduto: { etichetta: 'Scaduto', classi: 'bg-soft text-ink-muted' },
};

function titolo(e: Esportazione): string {
  return `${CATALOGO_EXPORT[e.dataset].etichetta} · ${e.formato === 'xlsx' ? 'Excel' : 'CSV'}`;
}

function dettagli(e: Esportazione): string {
  const parti = [formattaDataOra(e.creatoIl)];
  if (e.righe != null) parti.push(`${formattaNumero(e.righe)} righe`);
  if (e.fogli && e.fogli > 1) parti.push(`${e.fogli} fogli`);
  parti.push(
    e.filtriAttivi
      ? `${e.filtriAttivi} ${e.filtriAttivi === 1 ? 'filtro' : 'filtri'}`
      : 'senza filtri',
  );
  if (e.stato === 'pronto' && e.scadeIl)
    parti.push(`scaricabile fino al ${formattaData(e.scadeIl)}`);
  return parti.join(' · ');
}

const inCorso = ref<number | null>(null);

async function scarica(e: Esportazione): Promise<void> {
  inCorso.value = e.id;
  try {
    // Navigazione, non fetch: il file arriva dallo storage con il suo nome.
    window.location.href = await linkDownloadExport(e.id);
  } catch (errore) {
    avvisi.errore(
      'Download non riuscito',
      errore instanceof EvolfinApiError ? errore.message : 'Riprova tra qualche istante.',
    );
    await invalidate();
  } finally {
    inCorso.value = null;
  }
}

async function riprova(e: Esportazione): Promise<void> {
  inCorso.value = e.id;
  try {
    await riprovaExport(e.id);
    await invalidate();
  } catch (errore) {
    avvisi.errore(
      'Non è stato possibile riprovare',
      errore instanceof EvolfinApiError ? errore.message : 'Riprova tra qualche istante.',
    );
  } finally {
    inCorso.value = null;
  }
}

const vuoto = computed(() => !isLoading.value && !esportazioni.value?.length);
</script>

<template>
  <div class="flex max-w-4xl flex-col gap-6">
    <header>
      <p class="text-xs font-semibold tracking-[0.08em] text-ink-muted uppercase">Export</p>
      <h1 class="mt-1 font-display text-[33px] leading-[1.07] font-semibold">I miei export</h1>
      <p class="mt-1 text-[13px] text-ink-secondary">I file restano scaricabili per 7 giorni.</p>
    </header>

    <StatoCaricamento v-if="isLoading" />

    <StatoVuoto
      v-else-if="vuoto"
      :icona="FileDown"
      titolo="Nessun export"
      descrizione="Si chiedono dal pulsante Esporta delle liste, alla voce «Esporta avanzato»."
    />

    <ul v-else class="card flex flex-col divide-y divide-line-soft overflow-hidden">
      <li
        v-for="e in esportazioni"
        :key="e.id"
        class="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5 sm:px-5"
      >
        <div class="min-w-[12rem] flex-1">
          <div class="flex flex-wrap items-center gap-2">
            <p class="text-[14px] font-medium text-ink">{{ titolo(e) }}</p>
            <span
              class="rounded-pill px-2 py-0.5 text-[11.5px] font-semibold"
              :class="STATI[e.stato].classi"
            >
              {{ STATI[e.stato].etichetta }}
            </span>
          </div>
          <p class="mt-0.5 text-[12.5px] text-ink-secondary">{{ dettagli(e) }}</p>
          <p v-if="e.errore" class="mt-0.5 text-[12.5px] text-[var(--color-danger-text)]">
            {{ e.errore }}
          </p>
        </div>

        <PulsanteAzione
          v-if="e.stato === 'pronto'"
          variante="contorno"
          :caricamento="inCorso === e.id"
          @click="scarica(e)"
        >
          Scarica
          <template #icona><Download :size="15" :stroke-width="1.9" /></template>
        </PulsanteAzione>
        <PulsanteAzione
          v-else-if="e.stato === 'fallito'"
          variante="contorno"
          :caricamento="inCorso === e.id"
          @click="riprova(e)"
        >
          Riprova
          <template #icona><RotateCw :size="15" :stroke-width="1.9" /></template>
        </PulsanteAzione>
      </li>
    </ul>
  </div>
</template>
